import React, { ReactNode } from 'react';
import { useTheme } from 'styled-components';

import {
  CompanyAdminRequestModel,
  ProjectUserRequestModel,
} from '../../../../../typings/api/skymap/rest/v1/.common';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { publishNotification } from '../../../utils/notifications/publish-notification';
import { textUtils } from '../../../utils/text/text';
import { Button } from '../button/button';
import { DataTable, DataTableSortType } from '../data-table/data-table';
import { Icon } from '../icon/icon';
import { InfoBox } from '../info-box/info-box';

type Item = {
  email: string;
  type: string;
  name: string;
  url: string;
  copyIcon: ReactNode;
  deleteIcon: ReactNode;
};

type State = 'Idle' | 'Loading' | 'Complete';
type ResultItem =
  | {
      type: 'project-user';
      request: ProjectUserRequestModel;
    }
  | {
      type: 'company-admin';
      request: CompanyAdminRequestModel;
    };

const InvitationList = () => {
  const theme = useTheme();
  const [state, setState] = React.useState<State>('Idle');
  const [items, setItems] = React.useState<Item[]>([]);
  const [hasError, setHasError] = React.useState(false);

  if (state !== 'Complete') {
    return (
      <>
        <Button
          color="primary"
          disabled={state === 'Loading'}
          loading={state === 'Loading'}
          variant="contained"
          onClick={async () => {
            try {
              setHasError(false);
              setState('Loading');

              const service = SkyMapAxiosServiceFactory.instance.createUserServiceV1();
              const companyAdminRequests: ResultItem[] = (
                await service.getCompanyAdminRequests({})
              ).data.map((request) => {
                return {
                  type: 'company-admin',
                  request,
                };
              });

              const projectUserRequests: ResultItem[] = (
                await service.getProjectUserRequests({})
              ).data.map((request) => {
                return {
                  type: 'project-user',
                  request,
                };
              });

              const newItems: Item[] = [];
              for (const item of [...projectUserRequests, ...companyAdminRequests]) {
                newItems.push({
                  email: item.request.email,
                  type: item.type === 'company-admin' ? 'Företagsadmin' : 'Projektdeltagare',
                  name:
                    item.type === 'company-admin'
                      ? item.request.companyName
                      : item.request.projectName,
                  url: item.request.url,
                  copyIcon: (
                    <Icon
                      color={theme.color.gray.medium}
                      icon="copy"
                      opacityhover={0.5}
                      onClick={() => {
                        textUtils.copyTextToClipboard(item.request.url);
                        publishNotification('succ0208');
                      }}
                    />
                  ),
                  deleteIcon: (
                    <Icon
                      color={theme.color.gray.medium}
                      icon="trash-alt"
                      opacityhover={0.5}
                      onClick={() => alert('Not implemented')}
                    />
                  ),
                });
              }

              setItems(newItems);
              setState('Complete');
            } catch {
              setState('Idle');
              setHasError(true);
            }
          }}
        >
          Hämta alla inbjudningar.
        </Button>
        {hasError && (
          <InfoBox color="red" topMargin={true}>
            Ett fel uppstod när inbjudningarna skulle hämtas.
          </InfoBox>
        )}
      </>
    );
  }

  return (
    <DataTable
      columns={{
        email: {
          sortableBy: { type: DataTableSortType.STRING },
          title: 'Inbjuden epost',
        },
        type: {
          sortableBy: { type: DataTableSortType.STRING },
          title: 'Typ',
        },
        name: {
          sortableBy: { type: DataTableSortType.STRING },
          title: 'Till',
        },
        url: {
          sortableBy: { type: DataTableSortType.STRING },
          unfilterable: true,
          title: 'Länk',
        },
        copyIcon: {
          unfilterable: true,
          width: '20px',
        },
        deleteIcon: {
          unfilterable: true,
          width: '20px',
        },
      }}
      fixedLayout={true}
      items={items}
      pageSize={15}
      tableStyle={{
        rowCell: {
          fontSize: '14px',
        },
        headerCell: {
          fontSize: '14px',
        },
      }}
    />
  );
};

export { InvitationList };
